import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface RuleEvent {
  id: string;
  ruleId: string;
  ruleName: string;
  orderNumber: string | null;
  orderId: string | null;
  actorName: string | null;
  outcome: "blocked" | "warned";
  reason: string;
  trigger: string | null;
  triggeredAt: string;
}

export default function RulesAudit() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<RuleEvent[]>({
    queryKey: ["/api/rules/audit/events"],
  });

  if (isLoading) return <div className="p-6">{t("common.loading")}</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="page-rules-audit">
      <h1 className="text-2xl font-bold">{t("rules.audit.title", "Rule violation history")}</h1>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("rules.audit.when", "When")}</TableHead>
              <TableHead>{t("rules.audit.rule", "Rule")}</TableHead>
              <TableHead>{t("rules.audit.outcome", "Outcome")}</TableHead>
              <TableHead>{t("rules.audit.order", "Order")}</TableHead>
              <TableHead>{t("rules.audit.actor", "Actor")}</TableHead>
              <TableHead>{t("rules.audit.reason", "Reason")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data || []).length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t("rules.audit.empty", "No rule triggers logged yet.")}</TableCell></TableRow>
            ) : (
              (data || []).map((e) => (
                <TableRow key={e.id} data-testid={`row-event-${e.id}`}>
                  <TableCell className="text-xs">{new Date(e.triggeredAt).toLocaleString()}</TableCell>
                  <TableCell>{e.ruleName}</TableCell>
                  <TableCell><Badge variant={e.outcome === "blocked" ? "destructive" : "secondary"}>{e.outcome}</Badge></TableCell>
                  <TableCell>{e.orderNumber || "—"}</TableCell>
                  <TableCell>{e.actorName || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.reason}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
